// @ts-nocheck
// Vendored from roblouie/gameboy-emulator with MultiNostalgia patches
// (see ../../PATCHES.md): the AudioContext + AudioWorkletNode + Vite
// worklet-URL import are gone. Audio output is now an inert no-op by
// default; consumers that want audio (the browser tier) drive it
// externally by reading from the public sound channels themselves.
// This makes the APU constructable in Node (no browser globals at
// module load) while leaving the timing / register state machine
// intact so any tick() side effects the rest of the system depends on
// still fire.
import { CPU } from "../cpu/cpu.js";
import { Sound1 } from "./sound-1.js";
import { Sound2 } from "./sound-2.js";
import { Sound4 } from "./sound-4.js";
import { Sound3 } from "./sound-3.js";
import { SoundsOnRegister } from "./registers/sound-control-registers/sounds-on-register.js";
import { SimpleByteRegister } from "../helpers/simple-byte-register.js";
export class APU {
    static FrameSequencerHertz = 512;
    FrameSequencerInterval = CPU.OperatingHertz / APU.FrameSequencerHertz;
    // Default to a hard-coded sample rate (matches typical AudioContext
    // defaults) so cyclesPerSample is well-defined in headless. Adapters
    // that need a different rate assign this.sampleRate before any tick();
    // the setter recomputes cyclesPerSample (a plain field would have left
    // the sample interval frozen at the 48 kHz default — wrong pitch /
    // sample count at any other rate).
    _sampleRate = 48000;
    get sampleRate() { return this._sampleRate; }
    set sampleRate(v) {
        this._sampleRate = v;
        this.cyclesPerSample = CPU.OperatingHertz / v;
        this.dcBlockPole = APU.dcBlockPoleFor(v);
    }
    cyclesPerSample = CPU.OperatingHertz / this._sampleRate;
    // MULTINOSTALGIA PATCH: DC-blocking high-pass filter on the audio OUTPUT.
    // Each channel's getSample() is UNIPOLAR (0..1) and drops to a hard 0 the
    // instant a channel goes inactive, so every note on/off, length-counter
    // expiry, and envelope step shifts the signal's DC level — an audible click
    // on every beat (a rhythmic crackle). Real DMG hardware removes this with a
    // series capacitor (a ~1-pole high-pass); emulating it centres the signal on
    // 0 and turns those steps into inaudible transients. Output-only state
    // (never read by the CPU, only touched in sampleChannels behind the
    // _isAudioEnabled gate), so the determinism contract is unaffected. See
    // PATCHES.md. Transfer fn: y[n] = x[n] - x[n-1] + pole * y[n-1].
    static dcBlockPoleFor(sampleRate) {
        // ~20 Hz cutoff, expressed rate-independently: pole = e^(-2*pi*fc/fs).
        return Math.exp((-2 * Math.PI * 20) / sampleRate);
    }
    dcBlockPole = APU.dcBlockPoleFor(this._sampleRate);
    dcBlockPrevIn = 0;
    dcBlockPrevOut = 0;
    frameSequencerCycleCounter = 0;
    sampleCycleCounter = 0;
    nr52SoundEndFlag = new SoundsOnRegister(0xff26);
    // MULTINOSTALGIA PATCH: NR50 (master volume, $FF24) and NR51 (sound
    // panning, $FF25) were not modeled at all — memory.ts had no case for
    // either, so reads fell through to its `default: 0xff` and writes went to
    // dead `memoryBytes`. That breaks any game that READ-MODIFY-WRITEs these
    // registers: Pokémon R/B's per-VBlank FadeOutAudio steps rAUDVOL ($FF24)
    // down until it reads back 0 to end a fade, but a constant 0xff read means
    // the fade never completes → wAudioFadeOutControl is never cleared → the
    // Poké Center heal wait-loop soft-locks and map-load music never switches.
    // Modeled as plain read/write byte registers (both are fully R/W on DMG).
    // See PATCHES.md.
    nr50MasterVolume = new SimpleByteRegister(0xff24);
    nr51SoundPanning = new SimpleByteRegister(0xff25);
    sound1;
    sound2;
    sound3;
    sound4;
    _isAudioEnabled = false;
    constructor() {
        this.sound1 = new Sound1();
        this.sound2 = new Sound2();
        this.sound3 = new Sound3();
        this.sound4 = new Sound4();
    }
    get isAudioEnabled() {
        return this._isAudioEnabled;
    }
    // MULTINOSTALGIA PATCH: NR52 ($FF26) reads compose the live channel-status
    // bits, and NR52 writes model APU power control — neither was modeled before
    // (reads returned the raw stored byte; writes just stored it).
    //
    // Read layout: bit 7 = power (the only writable bit), bits 4-6 read as 1
    // (unused), bits 0-3 = per-channel on/off status (read-only) sourced from
    // each channel's live isActive. Games poll bits 0-3 to wait for a note/SFX
    // to finish; before this they read back whatever was last written, which is
    // meaningless.
    readNr52() {
        const power = this.nr52SoundEndFlag.isAllSoundOn ? 0x80 : 0x00;
        const status = (this.sound1.isOn ? 0x01 : 0) |
            (this.sound2.isOn ? 0x02 : 0) |
            (this.sound3.isOn ? 0x04 : 0) |
            (this.sound4.isOn ? 0x08 : 0);
        return power | 0x70 | status;
    }
    // NR52 write: only bit 7 (power) is writable. Turning the APU off (bit 7:
    // 1 -> 0) resets every audio register to 0 and disables all channels, per
    // DMG hardware ("0 = disable the APU (resets all audio registers to 0)").
    writeNr52(value) {
        const wasOn = this.nr52SoundEndFlag.isAllSoundOn;
        const nowOn = (value & 0x80) !== 0;
        // Store only the power bit; the status bits are derived live in readNr52.
        this.nr52SoundEndFlag.value = value & 0x80;
        if (wasOn && !nowOn) {
            this.powerOff();
        }
    }
    powerOff() {
        this.sound1.powerOff();
        this.sound2.powerOff();
        this.sound3.powerOff();
        this.sound4.powerOff();
        this.nr50MasterVolume.value = 0;
        this.nr51SoundPanning.value = 0;
        // The frame sequencer is stopped and re-zeroed while the APU is off.
        this.frameSequencerStep = 0;
        this.frameSequencerCycleCounter = 0;
    }
    enableSound() {
        this._isAudioEnabled = true;
    }
    disableSound() {
        this._isAudioEnabled = false;
    }
    tick(cycles) {
        // MULTINOSTALGIA PATCH: do NOT early-return when audio is disabled.
        // Sound-emulation *state* (channel timers, length/envelope/sweep,
        // frame sequencer, isActive flags — all machine-visible via NR52 reads
        // and savestates) must advance identically regardless of whether the
        // host wants audio output. The _isAudioEnabled flag now gates only
        // sample GENERATION (see sampleChannels), never machine-visible state.
        // Previously the whole tick short-circuited, so a core run with
        // emulateSound=false (fidelity/headless tier) diverged from one with
        // sound on (browser tier) — a silent cross-tier false negative.
        this.sound1.tick(cycles);
        this.sound2.tick(cycles);
        this.sound3.tick(cycles);
        this.sound4.tick(cycles);
        this.sampleCycleCounter += cycles;
        if (this.sampleCycleCounter >= this.cyclesPerSample) {
            this.sampleChannels();
            this.sampleCycleCounter -= this.cyclesPerSample;
        }
        this.frameSequencerCycleCounter += cycles;
        if (this.frameSequencerCycleCounter >= this.FrameSequencerInterval) {
            this.advanceFrameSequencer();
            this.frameSequencerCycleCounter -= this.FrameSequencerInterval;
        }
    }
    // Output sink: adapters install this to receive sample batches. When
    // null (the default), generated samples are dropped. Patched in by
    // MultiNostalgia: upstream pushed samples to an AudioWorkletNode
    // unconditionally.
    onSampleBatch = null;
    tempBuffer = new Float32Array(1024);
    tempIndex = 0;
    getSample(isSoundOn) {
        if (!isSoundOn) {
            return 0;
        }
        const s1 = this.sound1.getSample();
        const s2 = this.sound2.getSample();
        const s3 = this.sound3.getSample();
        const s4 = this.sound4.getSample();
        // MULTINOSTALGIA PATCH: apply NR51 sound panning ($FF25) when mixing.
        // NR51 routes each channel to the left (SO2) and/or right (SO1) output;
        // a channel with BOTH its bits clear is silent. This isn't a stereo
        // nicety — games use it as a channel mute. Pokémon R/B rests the wave
        // channel (sound 3) by clearing its NR51 bits (audio/engine_1.asm:
        // Audio1_HWChannelDisableMasks) instead of touching NRx2/NR52, since the
        // wave channel has no volume-envelope to zero. The old mixer summed all
        // four channels unconditionally, so a channel the game had panned off
        // kept sounding — the sustained "hanging buzz" during the title theme.
        // NR51 bit layout: high nibble = left (SO2), low nibble = right (SO1);
        // within each nibble bit i selects channel i+1.
        const nr51 = this.nr51SoundPanning.value;
        let left = 0;
        let right = 0;
        if (nr51 & 0x10)
            left += s1;
        if (nr51 & 0x20)
            left += s2;
        if (nr51 & 0x40)
            left += s3;
        if (nr51 & 0x80)
            left += s4;
        if (nr51 & 0x01)
            right += s1;
        if (nr51 & 0x02)
            right += s2;
        if (nr51 & 0x04)
            right += s3;
        if (nr51 & 0x08)
            right += s4;
        left /= 4;
        right /= 4;
        // Apply NR50 master volume so software fades (FadeOutAudio steps rAUDVOL
        // down each frame) are actually audible instead of a hard cut. Each side
        // is a 0-7 level; the analog multiplier is (level+1)/8. We output mono, so
        // scale each side by its own master level and average the two. When both
        // sides are fully panned (NR51 = 0xFF) this reduces to the previous
        // formula exactly, so overall loudness is unchanged. Output-only — no
        // machine-visible state, so determinism is unaffected.
        const nr50 = this.nr50MasterVolume.value;
        const rightLevel = nr50 & 0b111;
        const leftLevel = (nr50 >> 4) & 0b111;
        left *= (leftLevel + 1) / 8;
        right *= (rightLevel + 1) / 8;
        return (left + right) / 2;
    }
    // One-pole DC-blocking high-pass (see dcBlockPoleFor above). Removes the
    // channels' unipolar DC bias so channel/note on-off steps don't click.
    dcBlock(x) {
        const y = x - this.dcBlockPrevIn + this.dcBlockPole * this.dcBlockPrevOut;
        this.dcBlockPrevIn = x;
        this.dcBlockPrevOut = y;
        return y;
    }
    sampleChannels() {
        const isSoundOn = this.nr52SoundEndFlag.isAllSoundOn;
        if (!isSoundOn) {
            this.frameSequencerCycleCounter = 0;
        }
        // MULTINOSTALGIA PATCH: the frame-sequencer-counter reset above is a
        // machine-visible side effect (it retimes length/envelope/sweep and is
        // captured in savestates), so it stays unconditional. Everything below
        // is pure OUTPUT — filling the sample buffer and emitting batches — and
        // is what _isAudioEnabled gates. getSample() has no side effects, so
        // skipping it leaves all machine-visible state untouched.
        if (!this._isAudioEnabled) {
            return;
        }
        this.tempBuffer[this.tempIndex] = this.dcBlock(this.getSample(isSoundOn));
        this.tempIndex++;
        if (this.tempIndex >= this.tempBuffer.length) {
            const out = this.tempBuffer;
            this.tempBuffer = new Float32Array(1024);
            this.tempIndex = 0;
            if (this.onSampleBatch)
                this.onSampleBatch(out);
        }
    }
    // Emit any partially-filled sample buffer immediately. Call at frame
    // boundaries: a GB frame is ~800 samples < the 1024 batch size, so
    // without an explicit flush a per-frame drainAudio() returns nothing on
    // most frames (and a 1024 lump on others). That quantization makes
    // per-frame activity measurement read genuinely-active frames as
    // silent. Flushing preserves total samples — it just hands out the
    // current frame's tail now instead of carrying it into the next batch.
    flushSampleBatch() {
        if (this.tempIndex === 0)
            return;
        const out = this.tempBuffer.subarray(0, this.tempIndex);
        this.tempBuffer = new Float32Array(1024);
        this.tempIndex = 0;
        if (this.onSampleBatch)
            this.onSampleBatch(out);
    }
    //  Frame Sequencer
    //  Step   Length Ctr  Vol Env     Sweep
    // ---------------------------------------
    //   0      Clock       -           -
    //   1      -           -           -
    //   2      Clock       -           Clock
    //   3      -           -           -
    //   4      Clock       -           -
    //   5      -           -           -
    //   6      Clock       -           Clock
    //   7      -           Clock       -
    // ---------------------------------------
    //  Rate   256 Hz      64 Hz       128 Hz
    frameSequencerStep = 0;
    advanceFrameSequencer() {
        switch (this.frameSequencerStep) {
            case 0:
                this.clockLength();
                break;
            case 2:
                this.clockLength();
                this.clockSweep();
                break;
            case 4:
                this.clockLength();
                break;
            case 6:
                this.clockLength();
                this.clockSweep();
                break;
            case 7:
                this.clockVolume();
                break;
        }
        this.frameSequencerStep++;
        if (this.frameSequencerStep === 8) {
            this.frameSequencerStep = 0;
        }
    }
    clockLength() {
        this.sound1.clockLength();
        this.sound2.clockLength();
        this.sound3.clockLength();
        this.sound4.clockLength();
    }
    clockSweep() {
        this.sound1.clockSweep();
    }
    clockVolume() {
        this.sound1.clockVolume();
        this.sound2.clockVolume();
        this.sound4.clockVolume();
    }
}
//# sourceMappingURL=apu.js.map